/**
 * "Somebody is writing right now."
 *
 * One tiny module-level fact, because two very different parts of the app need
 * it and neither can see the other:
 *
 *   The **pad** must not resize its canvas or reflow the page while a stroke is
 *   live. Safari collapses its toolbars *as you write*, which fires a
 *   ResizeObserver, which rebuilt the backing store and re-hydrated the page
 *   from staff-relative units — under the pen. That is the reported glitching.
 *
 *   The **navigation** must not act on a click that was really the end of a
 *   Pencil stroke. A stroke that starts on the manuscript and finishes over the
 *   bottom bar produces a perfectly ordinary `click` on a tab, and the learner
 *   is thrown into another screen mid-note.
 *
 * A React context would be the tidy answer and cannot serve the second case: the
 * navigation is a sibling of the pad, mounted above it, and the immersive layer
 * is portalled out of the tree entirely. This is deliberately a global, because
 * the thing it describes — a pen touching glass — is global.
 */

let depth = 0;
let endedAt = 0;
const listeners = new Set<(writing: boolean) => void>();

/**
 * How long after a stroke a click is still assumed to belong to it.
 *
 * Safari synthesises the compatibility mouse events *after* `pointerup`, so a
 * guard that only covered the live stroke would still let the trailing click
 * through. A quarter of a second is far longer than that gap and far shorter
 * than the pause before a deliberate tap on a tab.
 */
export const WRITING_SETTLE_MS = 250;

/**
 * Listeners are told about the *stroke*, not the settle window.
 *
 * `isWriting()` stays true for a moment after `pointerup` so the trailing
 * synthetic click can be ignored — but a subscriber watching for "the pen is
 * down" would then see `true` twice and never a `false`. The click guard reads
 * the function; a subscriber gets the fact.
 */
function notify() {
  for (const listener of listeners) listener(isStrokeActive());
}

/** True while a stroke is live, or has only just finished. */
export function isWriting(): boolean {
  if (depth > 0) return true;
  return endedAt > 0 && performance.now() - endedAt < WRITING_SETTLE_MS;
}

/** True only while a pointer is genuinely down. */
export function isStrokeActive(): boolean {
  return depth > 0;
}

export function beginWriting(): void {
  depth += 1;
  if (depth === 1) notify();
}

export function endWriting(): void {
  // Never below zero: a teardown mid-stroke can end a gesture that a remount
  // has already forgotten starting, and a negative depth would leave the app
  // permanently "not writing" — or permanently writing, which is worse.
  depth = Math.max(0, depth - 1);
  if (depth === 0) {
    endedAt = performance.now();
    notify();
  }
}

export function onWritingChange(listener: (writing: boolean) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam. Nothing in the app calls this. */
export function __resetWritingActivity(): void {
  depth = 0;
  endedAt = 0;
  listeners.clear();
}
